import { Controller } from "@hotwired/stimulus"
import Logger from '../lib/logger'

/**
 * Typing Indicator Controller
 * 
 * A reusable Stimulus controller for real-time typing indicator functionality.
 * Uses ActionCable to broadcast and receive typing status.
 * 
 * Usage:
 *   <div data-controller="typing-indicator"
 *        data-typing-indicator-channel-id-value="123"
 *        data-typing-indicator-channel-type-value="channel">
 *     <div class="d-none" data-typing-indicator-target="indicator">
 *       <span data-typing-indicator-target="text">Someone is typing...</span>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["indicator", "text", "dot"]
  static values = {
    channelId: Number,
    timeout: { type: Number, default: 3000 },
    debounce: { type: Number, default: 500 },
    mode: { type: String, default: "text" } // "text" or "dot"
  }

  connect() {
    this.typingUsers = new Map() // user_id -> { name, timeout }
    this.setupSubscription()
  }

  disconnect() {
    this.unsubscribe()
    this.clearAllTimeouts()
  }

  setupSubscription() {
    if (!this.hasChannelIdValue) return
    
    const cable = this.getActionCable()
    if (!cable) {
      Logger.warn("ActionCable not available for typing indicator")
      return
    }
    
    const params = { channel: "TypingChannel", channel_id: this.channelIdValue }
    
    this.subscription = cable.subscriptions.create(params, {
        received: (data) => this.handleReceived(data),
        connected: () => Logger.debug("Typing indicator connected"),
        disconnected: () => Logger.debug("Typing indicator disconnected")
      }
    )
  }

  // Get ActionCable consumer
  getActionCable() {
    // Try different ways ActionCable might be available
    if (typeof App !== "undefined" && App.cable) {
      return App.cable
    }
    if (typeof window.ActionCable !== "undefined" && window.ActionCable.createConsumer) {
      const url = document.head.querySelector('meta[name="action-cable-url"]')?.content
      return window.ActionCable.createConsumer(url)
    }
    return null
  }

  // Unsubscribe from channel
  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    clearTimeout(this.sendTypingTimeout)
  }

  // Clear all user typing timeouts
  clearAllTimeouts() {
    this.typingUsers.forEach(({ timeout }) => clearTimeout(timeout))
    this.typingUsers.clear()
  }

  // Handle received typing data
  handleReceived(data) {
    if (data.user_id == this.getCurrentUserId()) return // Ignore own typing
    
    if (data.typing) {
      this.addTypingUser(data.user_id, data.user_name)
    } else {
      this.removeTypingUser(data.user_id)
    }
    
    this.updateDisplay()
  }

  // Add a user to typing list
  addTypingUser(userId, userName) {
    // Clear existing timeout for this user
    const existing = this.typingUsers.get(userId)
    if (existing) {
      clearTimeout(existing.timeout)
    }
    
    // Set timeout to auto-remove after inactivity
    const timeout = setTimeout(() => {
      this.removeTypingUser(userId)
      this.updateDisplay()
    }, this.timeoutValue * 2)
    
    this.typingUsers.set(userId, { name: userName, timeout })
  }

  // Remove a user from typing list
  removeTypingUser(userId) {
    const user = this.typingUsers.get(userId)
    if (user) {
      clearTimeout(user.timeout)
      this.typingUsers.delete(userId)
    }
  }

  // Update the display
  updateDisplay() {
    const users = Array.from(this.typingUsers.values()).map(u => u.name)
    const isTyping = users.length > 0

    if (this.modeValue === "dot") {
      this.toggleDot(isTyping)
    } else {
      if (!isTyping) {
        this.hideIndicator()
      } else {
        this.showIndicator()
        this.updateText(users)
      }
    }
  }

  // Toggle dot visibility
  toggleDot(show) {
    if (this.hasDotTarget) {
      if (show) {
        this.dotTarget.classList.remove("d-none")
      } else {
        this.dotTarget.classList.add("d-none")
      }
    }
  }

  // Update typing text based on users
  updateText(users) {
    if (!this.hasTextTarget) return
    
    let text
    if (users.length === 1) {
      text = `${users[0]} is typing...`
    } else if (users.length === 2) {
      text = `${users[0]} and ${users[1]} are typing...`
    } else {
      text = `${users.length} people are typing...`
    }
    
    this.textTarget.textContent = text
  }

  showIndicator() {
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.classList.remove("d-none")
    }
  }

  hideIndicator() {
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.classList.add("d-none")
    }
  }

  // Called when user starts typing (from message composer)
  sendTyping() {
    if (!this.subscription) return
    
    clearTimeout(this.sendTypingTimeout)
    
    // Send typing start
    this.subscription.perform("typing", { typing: true })
    
    // Auto-send stop after timeout
    this.sendTypingTimeout = setTimeout(() => {
      this.sendStoppedTyping()
    }, this.timeoutValue)
  }

  // Called when user stops typing
  sendStoppedTyping() {
    if (!this.subscription) return
    
    clearTimeout(this.sendTypingTimeout)
    this.subscription.perform("typing", { typing: false })
  }

  // Get current user ID from meta tag or data attribute
  getCurrentUserId() {
    const meta = document.head.querySelector('meta[name="current-user-id"]')
    if (meta && meta.content) return parseInt(meta.content)
    
    const dataAttr = document.body.dataset.currentUserId
    if (dataAttr) return parseInt(dataAttr)
    
    return window.currentUserId || null
  }
}